// Simple options so callers can tune how aggressive retries are.
export interface RetryOptions {
    attempts: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffFactor?: number;
    jitter?: number;
    shouldRetry?: (error: unknown, attempt: number) => boolean;
}

// Small helper to pause between retry attempts.
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const withRetry = async <T>(
    fn: () => Promise<T>,
    options: RetryOptions
): Promise<T> => {
    // Apply defaults so callers can pass only what they care about.
    const {
        attempts,
        initialDelayMs = 300,
        maxDelayMs = 2000,
        backoffFactor = 2,
        jitter = 0.2,
        shouldRetry,
    } = options;

    // If there's only one attempt, just run the function.
    if (attempts <= 1) {
        return fn();
    }

    let lastError: unknown;

    // Try the request a few times with backoff.
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            // Avoid retrying on typical client errors (e.g., 400).
            const status = (error as { response?: { status?: number } })?.response?.status;
            const isClientError = typeof status === "number" && status >= 400 && status < 500;
            const retryAllowed = shouldRetry ? shouldRetry(error, attempt) : !isClientError;

            // Give up if the policy says so or this was the last attempt.
            if (!retryAllowed || attempt === attempts) {
                throw error;
            }

            // Exponential backoff with some jitter to avoid thundering herd.
            const baseDelay = initialDelayMs * Math.pow(backoffFactor, attempt - 1);
            const delay = Math.min(maxDelayMs, baseDelay);
            const jitterScale = jitter ? 1 + ((Math.random() * 2 - 1) * jitter) : 1;
            const delayWithJitter = Math.max(0, Math.round(delay * jitterScale));
            await sleep(delayWithJitter);
        }
    }

    // Safety fallback in case we exit the loop unexpectedly.
    throw lastError ?? new Error("Retry failed");
};
